import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

export default class extends Controller {
  static targets = ["panelContainer", "panel", "editButton"]

  connect() {
    this.editing = false
    this.sortable = null
    this.syncToggleStates()
  }

  syncToggleStates() {
    this.panelTargets.forEach(panel => {
      const isSmall = panel.classList.contains("dashboard-panel--small")
      const currentSize = isSmall ? "small" : "big"
      const pill = panel.querySelector(".size-toggle-pill")
      if (!pill) return

      pill.querySelectorAll(".size-toggle-option").forEach(opt => {
        const optSize = opt.dataset.size
        opt.classList.toggle("active", optSize === currentSize)
      })
    })
  }

  disconnect() {
    if (this.sortable) {
      this.sortable.destroy()
      this.sortable = null
    }
  }

  toggleEdit() {
    this.editing = !this.editing
    const container = this.element

    if (this.editing) {
      container.classList.add("editing-mode")
      this.editButtonTarget.textContent = this.editButtonTarget.dataset.doneText || "Done"
      this.editButtonTarget.classList.remove("btn-outline-secondary")
      this.editButtonTarget.classList.add("btn-primary")
      this.initSortable()
    } else {
      container.classList.remove("editing-mode")
      this.editButtonTarget.textContent = this.editButtonTarget.dataset.editText || "Edit Layout"
      this.editButtonTarget.classList.remove("btn-primary")
      this.editButtonTarget.classList.add("btn-outline-secondary")
      this.destroySortable()
    }
  }

  initSortable() {
    if (this.sortable) {
      this.sortable.destroy()
    }

    if (!this.hasPanelContainerTarget) return

    this.sortable = new Sortable(this.panelContainerTarget, {
      animation: 150,
      handle: ".panel-drag-handle",
      ghostClass: "sortable-ghost",
      dragClass: "sortable-drag",
      onEnd: () => {
        this.updatePositions()
      }
    })
  }

  destroySortable() {
    if (this.sortable) {
      this.sortable.destroy()
      this.sortable = null
    }
  }

  updatePositions() {
    const panels = this.panelContainerTarget.querySelectorAll("[data-panel-key]")
    const orderedKeys = Array.from(panels).map(el => el.dataset.panelKey)

    this.patchDashboard({
      action_type: "reorder",
      ordered_keys: orderedKeys
    })
  }

  togglePanel(event) {
    const panelKey = event.currentTarget.dataset.panelKey
    const panel = this.panelTargets.find(p => p.dataset.panelKey === panelKey)
    if (!panel) return

    const isVisible = !panel.classList.contains("dashboard-panel--hidden")
    panel.classList.toggle("dashboard-panel--hidden")

    // Update button icon
    const btn = event.currentTarget
    const icon = btn.querySelector("i")
    if (icon) {
      icon.classList.toggle("fa-eye")
      icon.classList.toggle("fa-eye-slash")
    }

    this.patchDashboard({
      action_type: "toggle",
      panel_key: panelKey,
      visible: String(!isVisible)
    })
  }

  resizePanel(event) {
    const btn = event.currentTarget
    const newSize = btn.dataset.size
    if (!newSize) return

    const panelKey = btn.dataset.panelKey || btn.closest("[data-panel-key]")?.dataset.panelKey
    const panel = this.panelTargets.find(p => p.dataset.panelKey === panelKey)
    if (!panel) return

    // Update panel size class
    panel.classList.remove("dashboard-panel--small", "dashboard-panel--big")
    panel.classList.add("dashboard-panel--" + newSize)

    // Update pill toggle active state
    const pill = btn.closest(".size-toggle-pill")
    if (pill) {
      pill.querySelectorAll(".size-toggle-option").forEach(opt => opt.classList.remove("active"))
      btn.classList.add("active")
    }

    this.patchDashboard({
      action_type: "resize",
      panel_key: panelKey,
      size: newSize
    })
  }

  patchDashboard(data) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const url = this.element.dataset.updateUrl

    fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        "Accept": "application/json"
      },
      body: JSON.stringify(data)
    }).catch(error => {
      console.error("Dashboard update failed:", error)
    })
  }
}
